import { apiFetch, snakeToCamel } from '@/core/api/client'

export interface InboundEmail {
  id: string
  emailId: string
  sender: string
  senderName: string
  recipient: string
  subject: string
  bodyText: string
  bodyHtml: string
  attachments: Array<{
    filename: string
    contentType: string
    size?: number
  }>
  isRead: boolean
  autoProcessed: boolean
  autoProcessResult: string | Record<string, unknown> | null
  source: string
  createdAt: string
  updatedAt: string
}

/** Fetch all inbound emails. */
export async function getInboundEmails(params?: {
  isRead?: boolean
  page?: number
  pageSize?: number
}): Promise<{ count: number; next: string | null; previous: string | null; results: InboundEmail[] }> {
  const query = new URLSearchParams()
  query.set('page_size', String(params?.pageSize ?? 50))
  if (params?.page) {query.set('page', String(params.page))}
  if (params?.isRead !== undefined) {query.set('is_read', String(params.isRead))}

  const res = await apiFetch(`/emails/inbox/?${query.toString()}`)
  if (!res.ok) {throw new Error('Failed to fetch inbox')}
  const data = await res.json()
  return {
    count: data.count,
    next: data.next,
    previous: data.previous,
    results: (data.results as unknown[]).map((r) => snakeToCamel<InboundEmail>(r)),
  }
}

/** Mark an inbound email as read. */
export async function markEmailAsRead(id: string): Promise<void> {
  const res = await apiFetch(`/emails/inbox/${id}/mark-read/`, { method: 'POST' })
  if (!res.ok) {throw new Error('Failed to mark email as read')}
}

/** Mark an inbound email as unread. */
export async function markEmailAsUnread(id: string): Promise<void> {
  const res = await apiFetch(`/emails/inbox/${id}/mark-unread/`, { method: 'POST' })
  if (!res.ok) {throw new Error('Failed to mark email as unread')}
}

/** Get unread count. */
export async function getUnreadCount(): Promise<number> {
  const res = await apiFetch('/emails/inbox/?is_read=false&page_size=1')
  if (!res.ok) {throw new Error('Failed to fetch unread count')}
  const data = await res.json()
  return data.count ?? 0
}
